import {
  GoabContainer,
  GoabFormItem,
  GoabSpacer,
  GoabBlock,
  GoabDropdown,
  GoabDropdownItem,
  GoabInput,
} from "@abgov/react-components";

import { useGenerationForm, useSessionManagement } from "../../hooks";

import DateOfBirthInput from "../../../../../MPP-Common-SPA/Widgets/ContactDetails/DateOfBirthInput.jsx";

export const PersonalInformationSection = () => {
  const {
    form,
    fieldValues,
    fieldErrors,
    fieldTouches,
    handleChange,
    handleBlur,
    isReady,
  } = useGenerationForm("PersonalInformation");

  const { getRefData, getResourceValue } = useSessionManagement();

  // Errors are handled by the component, pass through custom form to set errors.
  const dobForm = {
    values: {
      dob: fieldValues?.dob,
    },
    errors: {
      dob: fieldErrors?.dob,
    },
    touched: {
      dob: fieldTouches?.dob,
    },
    setFieldError: (field: string, error: any) => {
      if (field === "dob") {
        if (error) {
          form.setFieldError(
            "PersonalInformation.dob",
            getResourceValue({ key: "RESOURCES@form.dob.error" })
          );
        }
      }
    },
    setFieldValue: (field: string, value: any) => {
      if (field === "dob") {
        form.setFieldValue("PersonalInformation.dob", value);
      }
    },
  };

  // Returns early if form isn't ready.
  if (!isReady) return null;

  const givenNameError = fieldTouches.givenName && fieldErrors.givenName;
  const middleNameError = fieldTouches.middleName && fieldErrors.middleName;
  const surnameError = fieldTouches.surname && fieldErrors.surname;
  const genderError = fieldTouches.gender && fieldErrors.gender;

  return (
    <>
      <GoabContainer accent="thin">
        <h3>
          {getResourceValue({
            key: "RESOURCES@personalInformation.interactiveContainer.title",
          })}
        </h3>
        <GoabSpacer vSpacing="l" />
        <GoabBlock direction="row" gap="l">
          <GoabFormItem
            label={getResourceValue({
              key: "RESOURCES@form.givenName.label",
            })}
            error={givenNameError}
          >
            <GoabInput
              name="givenName"
              value={fieldValues.givenName}
              onChange={({name, value}) => handleChange(name, value)}
              onBlur={({name, value}) => handleBlur(name)}
              error={!!givenNameError}
            ></GoabInput>
          </GoabFormItem>
          <GoabFormItem
            label={getResourceValue({
              key: "RESOURCES@form.middleName.label",
            })}
            error={middleNameError}
          >
            <GoabInput
              name="middleName"
              value={fieldValues.middleName}
              onChange={({name, value}) => handleChange(name, value)}
              onBlur={({name, value}) => handleBlur(name)}
              error={!!middleNameError}
            ></GoabInput>
          </GoabFormItem>
        </GoabBlock>
        <GoabSpacer vSpacing="l" />
        <GoabBlock direction="row" gap="m">
          <GoabFormItem
            label={getResourceValue({
              key: "RESOURCES@form.surname.label",
            })}
            error={surnameError}
          >
            <GoabInput
              name="surname"
              value={fieldValues.surname}
              onChange={({name, value}) => handleChange(name, value)}
              onBlur={({name, value}) => handleBlur(name)}
              error={!!surnameError}
            ></GoabInput>
          </GoabFormItem>
        </GoabBlock>

        <GoabSpacer vSpacing="l" />
        <GoabBlock direction="row" gap="2xl">
          <DateOfBirthInput
            key={fieldValues.dob}
            form={dobForm}
            getResourceValue={getResourceValue}
            getRefData={getRefData}
            context={{ stepData: { dob: fieldValues.dob } }}
            // Custom setFormData was needed due to incompatibility with nested properties in parent form.
            setFormData={(updatedValues: { dob: any }) => {
              if (updatedValues.dob !== undefined) {
                form.setFieldValue(
                  "PersonalInformation.dob",
                  updatedValues.dob
                );
                form.setFieldError("PersonalInformation.dob", undefined);

                // Check to clear empty error object left over due to internal error handling not managing nesting.
                const currentErrors = { ...form.errors };
                const personalInfo = currentErrors.PersonalInformation;

                if (personalInfo) {
                  delete (personalInfo as any).dob;

                  if (Object.keys(personalInfo as any).length === 0) {
                    delete currentErrors.PersonalInformation;
                  }
                }

                form.setErrors(currentErrors);
              }
            }}
          />
          <GoabFormItem
            label={getResourceValue({ key: "RESOURCES@form.gender.label" })}
            error={fieldErrors.gender}
          >
            <GoabDropdown
              name="gender"
              value={fieldValues.gender}
              onChange={({name, value}) => handleChange(name!, value)}
              width="30ch"
              error={!!genderError}
            >
              <GoabDropdownItem value="M" label="Male" />
              <GoabDropdownItem value="F" label="Female" />
              <GoabDropdownItem value="X" label="Unspecified" />
            </GoabDropdown>
          </GoabFormItem>
        </GoabBlock>
      </GoabContainer>
      <GoabSpacer vSpacing="2xs" />
    </>
  );
};
